/* ============================================================
   Корень новой витрины «Федерация киберспорта СКО».

   Рендерится из App.js при SITE_MODE === 'fed'. Роут берётся из
   основного useAppStore (роутинг единый со старой платформой),
   поэтому редиректы после входа/выхода продолжают работать.

   Страницы платформы, которых нет в новом дизайне (подача
   заявки, уведомления, команды), рендерятся внутри новой
   обёртки — функциональность сохранена.
   ============================================================ */
import './fed.css';
import { useEffect } from 'react';
import { useFedHistory } from './useFedHistory';
import { useAppStore } from '../store';
import { useFedStore } from './store';
import { useFedContent } from './content';
import { parseFedRoute, goFedRoute } from './deeplink';

import FedLoader from './chrome/Loader';
import FedNavBar from './chrome/NavBar';
import FedMobileMenu from './chrome/MobileMenu';
import FedFooter from './chrome/Footer';
import FedBottomNav from './chrome/BottomNav';
import FedSponsorModal from './chrome/SponsorModal';
import FedApplyModal from './chrome/ApplyModal';
import { FedBackground } from './ui';

import FedHome from './pages/Home';
import FedTournaments from './pages/Tournaments';
import FedTournament from './pages/Tournament';
import FedNews from './pages/News';
import FedArticle from './pages/Article';
import FedWinners from './pages/Winners';
import FedLive from './pages/Live';
import FedAbout from './pages/About';
import FedPartners from './pages/Partners';
import FedGallery from './pages/Gallery';
import FedContacts from './pages/Contacts';
import FedProjects from './pages/Projects';
import FedProject from './pages/Project';
import FedRegulations from './pages/Regulations';
import FedNotFound from './pages/NotFound';
import FedProfile from './pages/Profile';
import { PrivacyPolicy, UserAgreement } from './pages/Legal';
import FedMessenger from './pages/Messenger';
import { useMessenger } from './messenger/store';
import FedAdminApp from './admin/AdminApp';
import FedSmmApp from './smm/SmmApp';

// Страницы старой платформы, сохранённые в новой витрине
import AuthPage from '../pages/Auth';
import RegisterPage from '../pages/Register';
import NotificationsPage from '../pages/Notifications';
import TeamsPage from '../pages/TeamsPage';
import TeamPage from '../pages/TeamPage';
import MatchesPage from '../pages/Matches';

// Декоративный арт-фон используется только на публичных страницах витрины.
// Рабочие и личные разделы (auth/profile/notifications/teams/admin/smm) остаются
// на нейтральном фоне, чтобы не терять читаемость интерфейса.
const PUBLIC_ART_ROUTES = new Set([
  'home', 'tournaments', 'tournament', 'news', 'article', 'winners',
  'live', 'stream', 'bracket', 'about', 'partners', 'gallery', 'contacts',
  'projects', 'project', 'regulations',
  'notfound',
  'privacy', 'terms',
  'messenger',
]);

/** Вход/регистрация — полноэкранная страница старой платформы (по просьбе
    заказчика). Если пользователь уже вошёл и не шёл со страницы (authReturn) —
    на форме делать нечего, уводим в личный кабинет. */
function FedAuthGate() {
  const user = useAppStore((s) => s.user);
  const authMode = useFedStore((s) => s.authMode);
  useEffect(() => {
    if (user && !useFedStore.getState().authReturn) useAppStore.getState().go('profile');
  }, [user]);
  return <AuthPage key={authMode} initialMode={authMode} />;
}

export default function FedApp() {
  const route = useAppStore((s) => s.route);
  const user = useAppStore((s) => s.user);
  const lang = useFedStore((s) => s.lang);
  const userId = user?.id;
  const setAuthMode = useFedStore((s) => s.setAuthMode);
  const tourId = useFedStore((s) => s.tourId);
  const articleId = useFedStore((s) => s.articleId);
  const projectId = useFedStore((s) => s.projectId);
  const messengerId = useFedStore((s) => s.messengerId);
  const tourTab = useFedStore((s) => s.tourTab);
  const teamJoinCode = useFedStore((s) => s.teamJoinCode);
  const adminSection = useFedStore((s) => s.adminSection);
  const adminEditId = useFedStore((s) => s.adminEditId);
  const adminTourTab = useFedStore((s) => s.adminTourTab);

  // Возврат туда, откуда пользователь ушёл на вход (напр. регистрация турнира).
  // Старая форма про authReturn не знает, поэтому ловим вход на уровне витрины.
  useEffect(() => {
    if (!user) return;
    const { authReturn, setAuthReturn } = useFedStore.getState();
    if (authReturn) {
      setAuthReturn(null);
      if (authReturn.extra) useFedStore.setState(authReturn.extra);
      useAppStore.getState().go(authReturn.route);
    }
  }, [user]);

  // класс режима на body (скроллбар, выделение, фон)
  useEffect(() => {
    document.body.classList.add('fed-mode');
    document.title = 'Федерация киберспорта СКО';
    return () => document.body.classList.remove('fed-mode');
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === 'kz' ? 'kk' : 'ru';
  }, [lang]);

  // контент витрины с бэкенда (новости, турниры, партнёры, настройки…)
  useEffect(() => { useFedContent.getState().hydrate(); }, []);

  // Список чатов и общий unread нужны в шапке на любой странице витрины.
  useEffect(() => {
    if (userId) useMessenger.getState().startListPolling();
    else useMessenger.getState().stopPolling();
    return () => useMessenger.getState().stopPolling();
  }, [userId]);

  // скролл к началу при смене страницы
  useEffect(() => { window.scrollTo(0, 0); }, [route]);

  // deep-link при загрузке: и hash (#news, #tournament/<id>), и «мёртвые» пути.
  // Для турнира/статьи восстанавливаем и id, иначе откроется дефолтный турнир.
  useEffect(() => {
    const raw = (window.location.hash || '').replace('#', '')
      || window.location.pathname.replace(/^\/+|\/+$/g, '');
    const parsed = parseFedRoute(raw);
    if (parsed && parsed.target !== 'home') goFedRoute(parsed);
    else if (raw && !/^(access_token|token|code)=/i.test(raw)) useAppStore.getState().go('notfound');
  }, []);

  // держим адресную строку в чистоте: '/' для главной, '/#роут' для остальных.
  // У турнира/статьи прошиваем id, чтобы обновление страницы открывало то же.
  useFedHistory(route, { tourId, tourTab, teamJoinCode, articleId, projectId, messengerId, adminSection, adminEditId, adminTourTab });

  // легаси-роут signup → auth в режиме регистрации
  useEffect(() => { if (route === 'signup') setAuthMode('signup'); }, [route, setAuthMode]);

  // Админка — отдельный полноэкранный shell без шапки/футера витрины
  if (route === 'admin') {
    return (
      <>
        <FedBackground />
        <div className={`fed-root fed-workspace-root fed-lang-${lang}`}>
          <FedAdminApp />
        </div>
      </>
    );
  }

  // SMM-панель — тот же полноэкранный shell, но только контент-разделы
  if (route === 'smm') {
    return (
      <>
        <FedBackground />
        <div className={`fed-root fed-workspace-root fed-lang-${lang}`}>
          <FedSmmApp />
        </div>
      </>
    );
  }

  const page = {
    home: <FedHome />,
    tournaments: <FedTournaments />,
    tournament: <FedTournament />,
    news: <FedNews />,
    article: <FedArticle />,
    winners: <FedWinners />,
    live: <FedLive />,
    stream: <FedLive />, // легаси-алиас
    bracket: <FedTournament />, // легаси-алиас (сетка FIFA)
    about: <FedAbout />,
    partners: <FedPartners />,
    gallery: <FedGallery />,
    contacts: <FedContacts />,
    projects: <FedProjects />,
    project: <FedProject />,
    regulations: <FedRegulations />,
    notfound: <FedNotFound />,
    auth: <FedAuthGate />,
    signup: <FedAuthGate />,
    profile: <FedProfile />,
    privacy: <PrivacyPolicy />,
    terms: <UserAgreement />,
    messenger: <FedMessenger />,
    // страницы платформы (функциональность сохранена)
    register: <RegisterPage />,
    notifications: <NotificationsPage />,
    teams: <TeamsPage />,
    team: <TeamPage />,
    matches: <MatchesPage />,
  }[route] || <FedNotFound />;

  return (
    <>
      <FedLoader />
      <FedBackground artwork={PUBLIC_ART_ROUTES.has(route)} home2026={route === 'home'} />
      <div className={`fed-root fed-main-pad fed-lang-${lang}`}>
        <FedNavBar />
        <FedMobileMenu />
        <main className="flex-1">{page}</main>
        {route !== 'messenger' && <FedFooter />}
      </div>
      <FedBottomNav />
      <FedSponsorModal />
      <FedApplyModal />
    </>
  );
}
